import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { fmt, parseDur } from './defaults';

// Two-column editor for Categories + Speech Types. Both live in the same
// modal so operators can tweak durations/colors and immediately see the
// clocks update (parent passes `onChange` callbacks).
export default function CategoriesEditor({
  categories,
  speechTypes,
  onCategoriesChange,
  onSpeechTypesChange,
  readOnly = false,
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Table
        title="Πίνακας ΚΑΤΗΓΟΡΙΕΣ"
        hint="Κάθε αλλαγή εδώ ενημερώνει αμέσως τα dropdown, τους χρόνους και τα ρολόγια."
        items={categories}
        onChange={onCategoriesChange}
        readOnly={readOnly}
        testIdPrefix="cat"
      />
      <Table
        title="Πίνακας ΤΥΠΟΣ ΟΜΙΛΙΑΣ"
        hint="Όταν η κατηγορία μιας γραμμής είναι «Ομιλία», η διάρκεια παίρνεται από τον επιλεγμένο ΤΥΠΟ."
        items={speechTypes}
        onChange={onSpeechTypesChange}
        readOnly={readOnly}
        testIdPrefix="speech"
      />
    </div>
  );
}

function Table({ title, hint, items, onChange, readOnly, testIdPrefix }) {
  const setItem = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([...items, { name: 'Νέο', sec: 60, color: '#888888' }]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-slate-800">
      <h4 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wide mb-2">{title}</h4>
      <div className="max-h-[280px] overflow-auto">
        <table className="w-full text-[11px] border-collapse">
          <thead className="sticky top-0 bg-[#1B2A4A] text-white">
            <tr>
              <th className="px-1 py-1 text-left">Όνομα</th>
              <th className="px-1 py-1 w-16">Διάρκεια</th>
              <th className="px-1 py-1 w-14">Χρώμα</th>
              <th className="px-1 py-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                <td className="px-1 py-0.5">
                  <input
                    data-testid={`${testIdPrefix}-${i}-name`}
                    value={it.name}
                    onChange={(e) => setItem(i, { name: e.target.value })}
                    disabled={readOnly}
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] text-slate-800 bg-white"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <input
                    data-testid={`${testIdPrefix}-${i}-dur`}
                    defaultValue={fmt(it.sec)}
                    onBlur={(e) => {
                      const v = parseDur(e.target.value);
                      setItem(i, { sec: v });
                      e.target.value = fmt(v);
                    }}
                    disabled={readOnly}
                    className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] font-mono text-center text-slate-800 bg-white"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <input
                    type="color"
                    data-testid={`${testIdPrefix}-${i}-color`}
                    value={it.color}
                    onChange={(e) => setItem(i, { color: e.target.value })}
                    disabled={readOnly}
                    className="w-full h-6 border border-gray-300 rounded cursor-pointer"
                  />
                </td>
                <td className="px-1 py-0.5 text-center">
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => del(i)}
                      data-testid={`${testIdPrefix}-${i}-del`}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={add}
          data-testid={`${testIdPrefix}-add-btn`}
          className="mt-2 text-xs bg-[#1B2A4A] text-white px-2 py-1 rounded hover:opacity-90 flex items-center gap-1"
        >
          <Plus size={12} /> Προσθήκη
        </button>
      )}
      <p className="text-[10px] text-gray-500 mt-2 leading-snug">{hint}</p>
    </div>
  );
}
